import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { demoInfo } from '@/shared/lib/demo';
import { watchNetwork } from './network';
import { dropForeign, pending } from './outbox';
import type { OfflineBootProps } from './OfflineBoot.types';
import { meId, persistSnapshot, restoreSnapshot } from './snapshot';
import { startSync } from './sync';

/** Дольше этого не ждём копию с диска: лучше показать страницу, чем держать пустой экран. */
const RESTORE_MS = 1500;

/**
 * Запуск офлайн-режима: вернуть копию данных с устройства до первого показа (иначе без сети страница
 * решит, что вы не вошли), следить за связью, отправлять очередь и сохранять копию. В демо копии нет.
 */
export function OfflineBoot({ children }: OfflineBootProps) {
  const client = useQueryClient();
  const demo = demoInfo() !== null;
  const [ready, setReady] = useState(demo);
  useEffect(() => {
    const stopNetwork = watchNetwork();
    if (demo) {
      const stopSync = startSync(client);
      return () => {
        stopNetwork();
        stopSync();
      };
    }
    let stopSync: (() => void) | null = null;
    let stopPersist: (() => void) | null = null;
    let alive = true;
    // Сначала общая копия (в ней «кто я»), затем история бесед «Доступно офлайн» этого человека.
    const restore = pending()
      .then((items) => restoreSnapshot(client, new Set(items.map((i) => i.id))))
      .then(async () => {
        const uid = meId(client);
        // Скачивание и склейка истории — отдельным файлом: в первую загрузку не попадают.
        if (uid !== null) await import('./history').then((m) => m.restoreHistory(client, uid));
      });
    const limit = new Promise<void>((resolve) => window.setTimeout(resolve, RESTORE_MS));
    void Promise.race([restore.catch(() => false), limit]).then(() => {
      if (!alive) return;
      stopPersist = persistSnapshot(client, {
        onSignedOut: () => undefined,
        onUser: (id) => {
          void dropForeign(id);
        },
      });
      stopSync = startSync(client);
      setReady(true);
    });
    return () => {
      alive = false;
      stopNetwork();
      stopPersist?.();
      stopSync?.();
    };
  }, [client, demo]);
  return ready ? children : null;
}
